import { createEntityAdapter, nanoid } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { format } from "date-fns";
import { userRoles } from "../../enums/userRoles";
import { buildQueryParams } from "../../utils/commonMethods";

const tasksAdapter = createEntityAdapter({
  selectId: (task) => task.id,
});

const taskDetailsAdapter = createEntityAdapter({
  selectId: (taskDetail) => taskDetail.id,
});

const taskStatusAdapter = createEntityAdapter({
  selectId: (status) => status.id,
});

const taskInsightsAdapter = createEntityAdapter({
  selectId: (insight) => insight.id,
});

const tasksInitialState = tasksAdapter.getInitialState();
const taskDetailsInitialState = taskDetailsAdapter.getInitialState();
const taskStatusInitialState = taskStatusAdapter.getInitialState();
const taskInsightsInitialState = taskInsightsAdapter.getInitialState();

export const taskSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query({
      query: ({
        role,
        page,
        statusId,
        assignedTo,
        query,
        typeId,
        subTypeId,
        groupId,
        insightsStatus,
        incidentTaskOrderBy,
        orderByType,
        departmentId,
        subDepartmentId,
        areaId,
        costCenterId,
        createdById,
        localMangerId,
        seniorMangerId,
        incidentAdvisorId,
      }) => {
        const params = {
          Page: page,
          StatusId: statusId,
          Query: query,
          TypeId: typeId,
          SubTypeId: subTypeId,
          GroupId: groupId,
          AssignedTo: assignedTo,
          InsightsStatus: insightsStatus,
          IncidentTaskOrderBy: incidentTaskOrderBy,
          OrderByType: orderByType,
          departmentId,
          subDepartmentId,
          areaId,
          costCenterId,
          createdById,
          localMangerId,
          seniorMangerId,
          incidentAdvisorId,
        };

        const queryString = buildQueryParams(params);

        return `/incident-tasks${
          role.includes(userRoles.ADMIN) ? "" : "/my"
        }?${queryString}`;
      },
      transformResponse: (responseData) => {
        const loadedTasks = [].concat(
          ...(responseData.items || responseData).map((item) => {
            const {
              progress,
              assignedTo,
              // createdBy,
              createdOn,
              dueOn,
              incident,
              name,
              // notes,
              status,
              // updatedOn,
              id,
              guid,
            } = item;

            return {
              id,
              guid,
              name,
              dueOn: format(dueOn, "dd/MM/yyyy"),
              location: incident?.costCenter
                ? incident?.costCenter?.description
                : "-",
              status,
              createdOn: format(createdOn, "dd/MM/yyyy"),
              assignedTo,
              incident,
              progress,
              pageSettings: {
                pageCount: responseData?.totalPages,
                pageSize: responseData?.pageSize,
                totalRecordsCount: responseData?.total,
                currentPage: responseData?.page,
              },
            };
          })
        );

        return tasksAdapter.setAll(tasksInitialState, loadedTasks);
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "Tasks", id: "ERROR" }];
        }
        return [
          { type: "Tasks", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "Tasks", id })),
        ];
      },
    }),
    getTaskStatus: builder.query({
      query: () => `/incident-tasks/statuses`,
      transformResponse: (responseData) => {
        const loadedTaskStatus = responseData;
        return taskStatusAdapter.setAll(
          taskStatusInitialState,
          loadedTaskStatus
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "TaskStatus", id: "ERROR" }];
        }

        return [
          { type: "TaskStatus", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "TaskStatus", id })),
        ];
      },
    }),
    getTaskDetails: builder.query({
      query: (id) => `/incident-tasks/${id}`,
      transformResponse: (responseData) => {
        const loadedTaskDeails = responseData;
        return taskDetailsAdapter.upsertOne(
          taskDetailsInitialState,
          loadedTaskDeails
        );
      },
      providesTags: (result, error, arg) => [
        { type: "TaskDetails", id: "LIST" },
      ],
    }),
    getTaskInsights: builder.query({
      query: () => `/insights/incident-tasks`,
      transformResponse: (responseData) => {
        const loadedTaskInsights = {
          ...responseData,
          id: nanoid(),
        };
        return taskInsightsAdapter.upsertOne(
          taskInsightsInitialState,
          loadedTaskInsights
        );
      },
      providesTags: (result, error, arg) => [
        { type: "TaskInsights", id: result?.id },
      ],
    }),
    updateTask: builder.mutation({
      query: (data) => ({
        url: `/incident-tasks/${data.id}`,
        method: "PUT",
        body: {
          name: data.name,
          description: data.description,
          notes: data.notes,
          dueDate: data.dueDate,
        },
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "TaskInsights" },
        { type: "IncidentInsights" },
      ],
    }),
    completeTask: builder.mutation({
      query: (id) => ({
        url: `/incident-tasks/${id}:complete`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "TaskInsights" },
        { type: "IncidentInsights" },
      ],
    }),
    updateTaskAssignee: builder.mutation({
      query: ({ id, assignedTo }) => ({
        url: `/incident-tasks/${id}/assignees`,
        method: "PUT",
        body: {
          assignedTo,
        },
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
      ],
    }),
    updateTaskProgress: builder.mutation({
      query: (data) => ({
        url: `/incident-tasks/${data.id}/progress`,
        method: "PUT",
        body: {
          progress: data.progress,
        },
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "TaskInsights" },
        { type: "IncidentInsights" },
      ],
    }),
    closeTask: builder.mutation({
      query: (id) => ({
        url: `/incident-tasks/${id}:close`,
        method: "PUT",
      }),
      invalidatesTags: [
        { type: "Tasks", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "TaskInsights" },
        { type: "IncidentInsights" },
      ],
    }),
    linkDocument: builder.mutation({
      query: ({ id, attachmentDocumentId }) => ({
        url: `/incident-tasks/${id}/linked-documents`,
        method: "POST",
        body: {
          attachmentDocumentId,
        },
      }),
      invalidatesTags: [
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
      ],
    }),
    deleteDocument: builder.mutation({
      query: (id) => ({
        url: `/incident-tasks/linked-documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
      ],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskDetailsQuery,
  useGetTaskStatusQuery,
  useCompleteTaskMutation,
  useLinkDocumentMutation,
  useGetTaskInsightsQuery,
  useCloseTaskMutation,
  useUpdateTaskAssigneeMutation,
  useUpdateTaskProgressMutation,
  useUpdateTaskMutation,
  useDeleteDocumentMutation,
} = taskSlice;
